/**
 * 파일명 : lap0102m.jsx
 * 설명 : 휴가신청 화면
 * 수정내역
 ************************************************
 * Version	   수정일자			수정자	    수정내용      
 ************************************************
 * 1.0		2020-08-11		윤성수	    최초작성
 **/

/************************************************
 * Import 영역  
 ************************************************/
/* React */
import React from 'react';
import locale from 'antd/lib/locale/ko_KR';
import { useReactToPrint } from 'react-to-print';

/* Reducer */
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { Usr0102mAction } from '../../../redux/slice/usr/usr0102m_slice';
import { IApprRefUsrItemState, IOffReqInfoState, IOffDcdState } from '../../../redux/models/usr/usr0102m_model';

/* CSS */

/* AntD */
import { Layout, Row, Col, Space, Descriptions, Input, DatePicker, Button, Typography, ConfigProvider, Select  } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

/* 업무 */
import * as commonProxy from 'libs/CommonProxy';
import * as common from 'libs/common';
import * as commonDate from 'libs/commonDate';
import dayjs from 'dayjs';
import { IApprListState } from 'redux/models/com/com0101m_model';

const Usr0102m = (props:any) => {
    /************************************************
     * 화면 초기화 영역  
     ************************************************/
	/** State / Ref / Reducer 선언 **/
    const dispatch = useAppDispatch();
    const componentRef = React.useRef(null);
    const offReqInfo     = useAppSelector((state) => state.Usr0102m.offReqInfo);
    const offDcdList     = useAppSelector((state) => state.Usr0102m.offDcdList);
    const approvalList   = useAppSelector((state) => state.Usr0102m.approvalList);
    const apprOpns       = useAppSelector((state) => state.Usr0102m.apprOpns);
    const refList        = useAppSelector((state) => state.Usr0102m.refList);


    /** Render 변수 선언영역 **/
    /* AntD */
    const { Content }     = Layout;
    const { RangePicker } = DatePicker;
    const { TextArea }    = Input;
    const { Text, Title } = Typography;



    /************************************************
     * 화면 초기화 이벤트
     ************************************************/
    /**
     * 기능 : componentDidMount
     * 설명 : 화면 최초 로드시에 필요한 기능을 구현한다.
     * param : 
     * return : None
     **/
    React.useEffect(() => {
        if (!offDcdList || offDcdList.length < 1) {
            setOffDcdList();
        }

        return () => {
            dispatch(Usr0102mAction.initState());
        }
    }, []);


    /**
     * 기능 : 휴가구분코드생성완료
     * 설명 : 화면 최초 로드시에 필요한 기능을 구현한다.
     * param : 
     * return : None
     **/
     React.useEffect(() => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터에 결재번호가 없으면 현재일 기준의 영업일수를 조회해서 요청일수를 셋팅한다.
        if (offDcdList && offDcdList.length > 0) {
            if (!props || common.isEmptyString(props.apprNo)) {
                const initOffReqInfo:IOffReqInfoState = {
                    ...offReqInfo,
                    empNo : common.getEmpNo(),
                    offDcd  : fn_CurrOffDcd().value
                };
                dispatch(Usr0102mAction.setOffReqInfo(initOffReqInfo));
    
                fn_SearchBizDays(null, null, null);
            }
            // 입력파라미터에 결재번호가 있으면 휴가정보를 조회한다.
            else {
                fn_SearchOffrInfo(props.apprNo);
            }
        }
    }, [offDcdList]);

    /**
     * 기능 : setOffDcdList
     * 설명 : 휴가구분코드생성
     * param : 
     * return : None
     **/
    const setOffDcdList = () => {
        const option = {
            useYn: 'Y',
            callback: (datas:IOffDcdState[]) => {
                let arrOptions:IOffDcdState[] = [];

                datas.forEach((item:IOffDcdState) => {
                    arrOptions.push({
                        label: item.cdNm,
                        value: item.cd,
                        attr1: item.attr1,
                        attr2: item.attr2,
                        rmk2 : item.rmk2,
                    });
                });

                dispatch(Usr0102mAction.setOffDcdList(arrOptions));
                dispatch(Usr0102mAction.setOffReqInfo({ ...offReqInfo, offDcd : arrOptions[0].value }));
            }

        };
        common.getComCodeList('008', option);
    }


    /**
     *  기능 : 휴가구분 데이터 변경
     *  설명 : 휴가구분 데이터 변경
     *  param :
     *  return : None
     */
    React.useEffect(() => {
        const currOffItem:IOffDcdState = fn_CurrOffDcd();
        if (currOffItem.attr2 !== '1') {
            const _offReqInfo = {
                ...offReqInfo,
                offEdDt : offReqInfo.offStDt
            };
            dispatch(Usr0102mAction.setOffReqInfo(_offReqInfo));
        }

        // 자동참조
        if (currOffItem.value === '6') {
            setAutoRefs(refList);
        }
    }, [offReqInfo.offDcd]);

    const setAutoRefs = (data:IApprRefUsrItemState[]) => {
        const refs = [...data];
        const currOffItem:IOffDcdState = fn_CurrOffDcd();

        // 병가
        if (currOffItem.value === '6') {
            if (!common.isEmptyString(currOffItem.rmk2)) {
                const arrAutoRefs:string[] = currOffItem.rmk2.split(',').filter((item:string) => {
                    return item.split(':')[0] !== common.getEmpNo();
                }).filter((item:string) => {
                    const isIncluded = refs.some(item2 => item2.refEmpNo.includes(item.split(':')[0]));
                    return !isIncluded
                });

                const refItems:IApprRefUsrItemState = {
                    apprEmpNo: '',
                    apprOrd: '',
                    apprStCd: '',
                    apprStNm: '',
                    apprUserNm: '',
                    chkYn: 'N',
                    empNo: '',
                    refEmpNo: '',
                    refUserNm: '',
                    userNm: ''
                };

                for (let i = 0; i < arrAutoRefs.length; i++) {
                    refs.push({
                        ...refItems,
                        refEmpNo: arrAutoRefs[i].split(':')[0],
                        refUserNm: arrAutoRefs[i].split(':')[1]
                    });
                }

                
            }
        }
        dispatch(Usr0102mAction.setRefList(refs));
    }


    /************************************************
     * 개발자 함수 영역  
     ************************************************/
    /**
     * 기능 : fn_SearchBizDays
     * 설명 : 영업일조회
     * param : 
     * return : None
     **/
    const fn_SearchBizDays = (stDt:string, edDt:string, callbackFn:Function | null) => {
        // 입력파라미터
        const startDt = commonDate.maskDate(common.isEmptyString(stDt) ? commonDate.getToday() : stDt, 'YYYY-MM-DD');
        const endDt   = commonDate.maskDate(common.isEmptyString(edDt) ? commonDate.getToday() : edDt, 'YYYY-MM-DD');
        const requestData = {
            stDt : startDt,
            edDt : endDt
        };
        
        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd : 'TRCOMR0002',
            noLoading : true,
            success : (responseData:any) => {
                dispatch(Usr0102mAction.setOffReqInfo( { ...offReqInfo, offStDt : startDt.replace(/-/gi, ''), offEdDt : endDt.replace(/-/gi, ''), reqDaysCnt : responseData.bizDayCnt } ));

                if (callbackFn) {
                    callbackFn();
                }
            },
        });
    }


    /**
     * 기능 : fn_SearchOffrInfo
     * 설명 : 휴가정보조회
     * param : 
     * return : None
     **/
     const fn_SearchOffrInfo = (apprNo:string) => {
        // 입력파라미터
        const requestData = {
            apprNo
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd : 'TRUSRR0004',
            success : (responseData:any) => {
                const offInf = {...responseData.offInf, reqDaysCnt : responseData.offInf.useOffDays};
                dispatch(Usr0102mAction.setOffReqInfo(offInf));
                dispatch(Usr0102mAction.setApprovalList(responseData.apprItems));
                dispatch(Usr0102mAction.setRefList(responseData.apprRefItems));
    
                const apprOpns = !responseData.apprItems || responseData.apprItems.length < 1 ? null : responseData.apprItems.filter((e:IApprListState) => {
                    return e.apprOpn !== null && e.apprOpn !== '';
                });
                dispatch(Usr0102mAction.setApprOpns(apprOpns));
                
                const filterd = responseData.apprItems.filter((item:IApprListState) => {
                    return item.apprOpn !== null && item.apprOpn !== undefined && item.apprOpn !== '';
                });
    
                if (filterd && filterd.length > 0) {
                    dispatch(Usr0102mAction.setApprOpn(filterd[0].apprOpn));
                }
                
                fn_GetRemainOffDays(responseData.offInf.empNo, responseData.offInf.offStDt, offInf);
            },
        });
    }


    /**
     * 기능 : fn_GetRemainOffDays
     * 설명 : 잔여휴가일수조회
     * param : 
     * return : None
     **/
    const fn_GetRemainOffDays = (empNo:string, offStDt:string, offInf:IOffReqInfoState) => {
        /* 변수선언 및 셋팅 영역 */
         const requestData = {
            // 입력파라미터
            empNo  : empNo ? empNo : common.getEmpNo(),
            baseYy : offStDt ? offStDt.substring(0, 4) : commonDate.maskDate(commonDate.getToday(), 'YYYY'),
            apprNo : props && props.apprNo ? props.apprNo : '',
        };

        commonProxy.callAsyncSvc(requestData, {
            txCd : 'TRUSRR0002',
            noLoading : true,
            success : (responseData:any) => {
                const _offInf:IOffReqInfoState = { ...offInf, offDcd : offInf && offInf.offDcd ? offInf.offDcd : fn_CurrOffDcd().value };
                if (!_offInf || common.isEmptyString(_offInf.offStDt)) {
                    dispatch(Usr0102mAction.setOffReqInfo({ remainOffDays : responseData.userOffStatus.remainOffDays }));             
                } else {
                    dispatch(Usr0102mAction.setOffReqInfo({ ..._offInf, remainOffDays : responseData.userOffStatus.remainOffDays }));
                }
            },
        });
    }

    /**
     * 기능 : fn_CurrOffDcd
     * 설명 : 요청일자계산
     * param : 
     * return : None
     **/
     const fn_CurrOffDcd = () => {
        let item:IOffDcdState;
         
        if (!offDcdList || offDcdList.length < 1) {
             item = { cd : '', cdNm: '', value : '', label : '', attr1 : '', attr2: '' };
        } else {
            let offDcd = offReqInfo.offDcd;
    
            if (common.isEmptyString(offReqInfo.offDcd)) {
                offDcd = offDcdList[0].value;
            }
    
            item = offDcdList.filter((e:IOffDcdState) => {
                return e.value === offDcd;
            })[0];
        }

        return item;
    }

    /**
     * 기능 : fn_GetUseDays
     * 설명 : 요청일자계산
     * param : 
     * return : None
     **/
    const fn_GetUseDays = () => {
        let result = -1;
        let item:IOffDcdState;
        if (offReqInfo.apprNo) {
            result = offReqInfo.useOffDays;
        } else {
            item = fn_CurrOffDcd();

            if (item.attr1 === 'Y') result = 0;
            else if (item.attr2 === '1') result = offReqInfo.reqDaysCnt && offReqInfo.reqDaysCnt > 0 ? offReqInfo.reqDaysCnt : 0;
            else if (item.attr2 !== '1') result = offReqInfo.reqDaysCnt === 1 ? 0.5 : 0;
        }

        return result;
    }

    /**
     * 기능 : isEnableApprCls
     * 설명 : 결재취소가능여부계산
     * param : 
     * return : None
     **/
    const isEnableApprCls = () => {
        if (!offReqInfo.apprNo) {
            return false;
        }

        if (offReqInfo.mainApprStCd !== '02' && offReqInfo.mainApprStCd !== '03') {
            return false;
        }
        if (offReqInfo.mainApprStCd === '02') {
            const apprItems = approvalList.filter((item:IApprRefUsrItemState) => {
                return parseInt(item.apprOrd) === parseInt(offReqInfo.currApprOrd) - 1;
            });

            if (!apprItems || apprItems.length < 1 || apprItems[0].apprEmpNo !== common.getEmpNo()) {
                return false;
            }
        } else {
            const apprItems = approvalList.filter((item:IApprRefUsrItemState) => {
                return parseInt(item.apprOrd) === parseInt(offReqInfo.currApprOrd);
            });

            if (!apprItems || apprItems.length < 1 || apprItems[0].apprEmpNo !== common.getEmpNo() || apprItems[0].apprStCd !== '03') {
                return false;
            }
        }

        if (commonDate.maskDate(commonDate.getToday(), 'YYYYMMDD') > offReqInfo.offStDt) {
            return false;
        }

        return true;
    }

        /**
     * 기능 : isEnableApprDel
     * 설명 : 결재삭제가능여부계산
     * param : 
     * return : None
     **/
    const isEnableApprDel = () => {

        if (!offReqInfo.apprNo) {
            return false; // 결재번호 없으면 return
        }

        // mainApprStCd : 결재원장상태:03(승인) | apprStCd : 결재이력상태:03(승인)
        if (offReqInfo.mainApprStCd !== '03' && offReqInfo.apprStCd !== '03') {
            return false;
        }

        // 결재원장상태:03(승인)
        if (offReqInfo.mainApprStCd === '03' && offReqInfo.apprStCd === '03') {
            const apprItems = approvalList.filter((item:IApprRefUsrItemState) => {
                // 결재순번 == 현재결재순번
                return parseInt(item.apprOrd) === parseInt(offReqInfo.currApprOrd);
            });

            if (!apprItems || apprItems.length < 1 || apprItems[0].apprEmpNo !== common.getEmpNo() || apprItems[0].apprStCd !== '03') {
                return false;
            }
        } else {
            return false;
        }

        return true;
    }

	/**
     * 기능 : 휴가구분
     * param : 
     **/
    const offDcdNm = React.useCallback(() => {
        return fn_CurrOffDcd().label;
    }, [offReqInfo.offDcd]);
	
	/**
     * 기능 : 초기화 함수
     * param : 
     **/
    const fn_Reset = () => {
        dispatch(Usr0102mAction.initState());

        // 영업일조회
        fn_SearchBizDays(null, null, fn_GetRemainOffDays);
    };

    /**
     *  기능 : RangePicker 데이터 변경
     *  설명 : RangePicker 데이터 변경값을 받는다
     *  param :
     *  return : None
     */
     const calendar_onChange = (_:any, dateStrings:any) => {
        let startDate;
        let endDate;
        if(Array.isArray(dateStrings)){
            startDate = dateStrings[0].split('-').join('');
            endDate   = dateStrings[1].split('-').join('');
            dispatch(Usr0102mAction.setOffReqInfo({ ...offReqInfo, offStDt : startDate, offEdDt : endDate }));
        }else{
            startDate = dateStrings;
            endDate   = dateStrings;
            dispatch(Usr0102mAction.setOffReqInfo({ ...offReqInfo, offStDt : dateStrings, offEdDt : dateStrings }));
        }

        fn_SearchBizDays(startDate, endDate, null);
    };

    /**
     * 기능 : onClick 이벤트 선언
     * param : 
     */
     const onClick = async (e:any) => {
        const buttonName = e.currentTarget.name;
        switch (buttonName) {
            // 초기화
            case 'btn_reset': {
                fn_Reset();
                break;
            }

            // 목록보기
            case 'btn_list': {
                const param = {
                    menuId : props.opener ? props.opener : 'MUSR2021002',
                };
                common.openPage(param);
                break;
            }

            case 'btn_approval': {
                const accept = (data:[]) => {
                    dispatch(Usr0102mAction.setApprovalList(data));
                }
                const params:any = {};
                params.title = '결재자 선택';
                params.popupId = 'com0101p1';
                params.accept = accept;
                params.width = 1350
                common.openPopup(params);
                break;
            }

            case 'btn_ref': {
                const accept = (data:IApprRefUsrItemState[]) => {
                    setAutoRefs(data);
                }
                const params:any = {};
                params.title = '참조자 선택';
                params.popupId = 'com0101p2';
                params.accept = accept;
                params.width = 1350
                common.openPopup(params);
                break;
            }
            
            case 'btn_return': {
                if (offReqInfo.apprEmpNo !== common.getEmpNo()) {
                    const param = {
                        title : '위드정보 확인',
                        contents: '해당 건에대한 권한이 없습니다.',
                    };

                    common.openInfo(param);
                    return;
                }

                if(offReqInfo.apprStCd !== '01'){
                    const param = {
                        title : '위드정보 확인',
                        contents: '결재 진행상태를 확인하세요.',
                    };

                    common.openInfo(param);
                    return;
                }

                onBtnReturnClick();
                break;
            }
            
            case 'btn_apprCls': {
                const accept = (data:string) => {
                    const param = {
                        title : '위드정보 확인',
                        contents: '결재취소 처리 하시겠습니까?',
                        accept : () => {
                            /* 변수선언 및 셋팅 영역 */
                            const requestData = {
                                // 입력파라미터
                                ...offReqInfo,
                                apprDcd : '001',
                                reqEmpNo : offReqInfo.empNo,
                                apprD : [ { apprOpn : data } ]
                            };
                    
                            commonProxy.callAsyncSvc(requestData, {
                                txCd : 'TRCOMS0011',
                                success : (_responseData:any) => {
                                    const param = {
                                        title : '위드정보 확인',
                                        contents: '결재취소 처리 되었습니다.',
                                    };
                    
                                    common.openAlert(param);
                                    fn_SearchOffrInfo(offReqInfo.apprNo);
                                },
                            });
                        }
                    };
                    common.openConfirm(param);
                }
                const params:any = {};
                params.title = '결재취소 입력';
                params.popupId = 'com9998p';
                params.props = { width: 700 };
                params.width = 700;
                params.accept = accept;
                common.openPopup(params);
                break;
            }

            case 'btn_apprDel': {
                const param = {
                    title : '위드정보 확인',
                    contents: '결재삭제 처리 하시겠습니까?',
                    accept : () => {
                    /* 변수선언 및 셋팅 영역 */
                    const requestData = {
                        // 입력파라미터
                        ...offReqInfo,
                        instCd : '002',                // 기관코드(002:위드정보)
                        apprDcd : '001',               // 결재구분코드(001:휴가)
                        reqStDt  : offReqInfo.offStDt, // 요청시작일자
                        reqEmpNo : offReqInfo.empNo   // 요청사원번호
                    };
                    
                        commonProxy.callAsyncSvc(requestData, {
                            txCd : 'TRCOMS0014', // ApprController(TRCOMS0014)
                            success : (_responseData:any) => {
                                const param = {
                                    title : '위드정보 확인',
                                    contents: '결재삭제 처리 되었습니다.',
                                    accept: () => {
                                        
                                        common.openPage({ menuId: 'MCOM2021002' });
                                    }
                                };
                
                                common.openAlert(param);
                                //fn_SearchOffrInfo(offReqInfo.apprNo);
                            },
                        });
                    }
                };
                common.openConfirm(param);
                break;
            }
            
            case 'btn_appr': {
                if (offReqInfo.apprEmpNo !== common.getEmpNo()) {
                    const param = {
                        title : '위드정보 확인',
                        contents: '해당 건에대한 권한이 없습니다.',
                    };

                    common.openInfo(param);
                    return;
                }

                if(offReqInfo.apprStCd !== '01'){
                    const param = {
                        title : '위드정보 확인',
                        contents: '결재 진행상태를 확인하세요.',
                    };

                    common.openInfo(param);
                    return;
                }

                const param = {
                    title : '위드정보 확인',
                    contents: '결재처리 하시겠습니까?',
                    accept : () => {
                        /* 변수선언 및 셋팅 영역 */
                        const requestData = {
                            // 입력파라미터
                            ...offReqInfo
                        };
                
                        commonProxy.callAsyncSvc(requestData, {
                            txCd : 'TRUSRS0003',
                            success : (_responseData:any) => {
                                const param = {
                                    title : '위드정보 확인',
                                    contents: '승인 처리 되었습니다.',
                                };
                
                                common.openAlert(param);

                                fn_SearchOffrInfo(offReqInfo.apprNo);
                            },
                        });
                    }
                };
                common.openConfirm(param);

                break;
            }

            case 'btn_req': {
                const offEdYy = offReqInfo.offEdDt.replace(/-/gi, '').substring(0, 4);
                const currYy  = commonDate.maskDate(commonDate.getToday(), 'YYYY-MM-DD').split('-')[0];
                if (currYy < offEdYy) {
                    const param = {
                        title : '위드정보 확인',
                        contents: '휴가기간은 현재 년도보다 큰 날짜를 지정할 수 없습니다.',
                    };

                    common.openInfo(param);
                    return;
                }

                let filterList:IApprRefUsrItemState[];

                if (approvalList && approvalList.length > 0) {
                    filterList = approvalList.filter((item:IApprRefUsrItemState) => {
                        return item.apprEmpNo !== common.getEmpNo();
                    });
                }

                if (!filterList || filterList.length < 1) {
                    const param = {
                        title : '위드정보 확인',
                        contents: '결재선을 지정하세요.',
                    };

                    common.openInfo(param);
                    return;
                }

                if (common.isEmptyString(offReqInfo.offRsn)) {
                    const param = {
                        title : '위드정보 확인',
                        contents: '사유를 입력하세요.',
                        accept : () => {
                            document.getElementById('inputOffRsn').focus();
                        }
                    };

                    common.openInfo(param);
                    return;
                }

                const param = {
                    title : '위드정보 확인',
                    contents: '상신 하시겠습니까?',
                    accept : () => {
                        /* 변수선언 및 셋팅 영역 */
                        const requestData = {
                            // 입력파라미터
                            ...offReqInfo, halfOffDcd: fn_CurrOffDcd().attr2
                            , apprM : { apprD : filterList, apprRef: refList }
                        };

                        commonProxy.callAsyncSvc(requestData, {
                            txCd : 'TRUSRS0001',
                            success : (responseData:any) => {
                                const param = {
                                    title : '위드정보 확인',
                                    contents: '상신 처리되었습니다.',
                                };
                                common.openAlert(param);
                                fn_SearchOffrInfo(responseData.offInf.apprM.apprNo);
                            },
                        });
                    }
                };

                common.openConfirm(param);
                break;
            }

            case 'btn_cls': {
                const param = {
                    title : '위드정보 확인',
                    contents: '상신취소 하시겠습니까?',
                    accept : () => {
                        /* 변수선언 및 셋팅 영역 */
                        const requestData = {
                            // 입력파라미터
                            ...offReqInfo,
                            apprM : { apprNo : offReqInfo.apprNo }
                        };
                
                        commonProxy.callAsyncSvc(requestData, {
                            txCd : 'TRUSRS0002',
                            success : (_responseData:any) => {
                                const param = {
                                    title : '위드정보 확인',
                                    contents: '상신취소 처리 되었습니다.',
                                };
                
                                common.openAlert(param);

                                fn_Reset();
                            },
                        });
                    }
                };
                common.openConfirm(param);
                break;
            }

            case 'btn_apprOpns': {
                const params:any = {};
                params.title = '결재의견';
                params.popupId = 'com0109p';
                params.props = { width: 900, apprOpns : approvalList };
                params.width = 900;
                common.openPopup(params);
                break;
            }

            default:
        }
    };

	/**
     * 기능 : 반려버튼 클릭
     * 설명 : 반려버튼 클릭
     * param : 
     * return : None
     */
     const onBtnReturnClick = () => {
        const accept = (data:string) => {
            const param = {
                title : '위드정보 확인',
                contents: '반려처리 하시겠습니까?',
                accept : () => {
                    /* 변수선언 및 셋팅 영역 */
                    const requestData = {
                        // 입력파라미터
                        ...offReqInfo,
                        apprM : { apprD : [ { apprOpn : data } ] }
                    };
            
                    commonProxy.callAsyncSvc(requestData, {
                        txCd : 'TRUSRS0004',
                        success : (_responseData:any) => {
                            const param = {
                                title : '위드정보 확인',
                                contents: '반려처리 되었습니다.',
                            };
            
                            common.openAlert(param);

                            fn_Reset();
                        },
                    });
                }
            };
            common.openConfirm(param);
        }
        const params:any = {};
        params.title = '반려의견 입력';
        params.popupId = 'com9998p';
        params.props = { width: 700 };
        params.width = 700;
        params.accept = accept;
        common.openPopup(params);
    }

    const handlePrint = useReactToPrint({
        contentRef: componentRef,
        documentTitle: '출력_문서',
    });

	return (
        /* 화면 디자인 영역 */
        <Layout>
            <Content>
                <Row justify='end' style={{marginLeft: 20, marginRight: 20}}>
                    <Col>
                        <Space>
                            {!common.isEmptyString(offReqInfo.apprNo) ?
                            <Button name='btn_print' className='background-color-default-button' onClick={() => handlePrint?.()}>출력</Button>
                            : null}
                            <Button
                                name='btn_reset'
                                className='background-color-default-button'
                                onClick={onClick}
                            >
                                초기화
                            </Button>
                            <Button
                                name='btn_list'
                                className='background-color-default-button'
                                onClick={onClick}
                            >
                                목록보기
                            </Button>
                            {apprOpns && apprOpns.length > 0 ?
                            <Button
                                name="btn_apprOpns"
                                className="background-color-default-button"
                                onClick={onClick}
                            >
                                결재의견
                            </Button> : null}
                            {common.isEmptyString(offReqInfo.apprNo) ?
                            <>
                            <Button
                                name='btn_approval'
                                className='background-color-default-button'
                                icon={<SearchOutlined />}
                                onClick={onClick}
                                disabled={!common.isEmptyString(offReqInfo.apprNo)}
                            >
                                결재선
                            </Button>
                            <Button
                                name='btn_ref'
                                className='background-color-default-button'
                                icon={<SearchOutlined />}
                                onClick={onClick}
                                disabled={!common.isEmptyString(offReqInfo.apprNo)}
                            >
                                참조자
                            </Button>
                            </>
                            : null}
                            {isEnableApprDel() ?
                            <Button
                                name='btn_apprDel'
                                className='background-color-primary-button'
                                onClick={onClick}
                            >
                                결재삭제
                            </Button>
                            : null}
                            {!(offReqInfo.apprEmpNo !== common.getEmpNo() || offReqInfo.apprStCd !== '01') ?
                            <>
                            <Button
                                name='btn_return'
                                className='background-color-primary-button'
                                onClick={onClick}
                                disabled={offReqInfo.apprEmpNo !== common.getEmpNo() || offReqInfo.apprStCd !== '01'}
                            >
                                휴가반려
                            </Button>
                            <Button
                                name='btn_appr'
                                className='background-color-primary-button'
                                onClick={onClick}
                                disabled={offReqInfo.apprEmpNo !== common.getEmpNo() || offReqInfo.apprStCd !== '01'}
                            >
                                휴가결재
                            </Button>
                            </>
                            : null}
                            {isEnableApprCls() ?
                            <Button
                                name='btn_apprCls'
                                className='background-color-primary-button'
                                onClick={onClick}
                            >
                                결재취소
                            </Button>
                            : null}
                            {(offReqInfo.apprNo && offReqInfo.empNo === common.getEmpNo() && offReqInfo.mainApprStCd === '01') ?
                            <>
                            <Button
                                name='btn_cls'
                                className='background-color-primary-button'
                                onClick={onClick}
                                disabled={!(offReqInfo.apprNo && offReqInfo.empNo === common.getEmpNo() && offReqInfo.mainApprStCd === '01')}
                            >
                                상신취소
                            </Button>
                            </>
                            : null}
                            {common.isEmptyString(offReqInfo.apprNo) ?
                            <Button
                                name='btn_req'
                                className='background-color-primary-button'
                                onClick={onClick}
                            >
                                휴가상신
                            </Button>
                            : null}
                        </Space>
                    </Col>
                </Row>
                <div ref={componentRef} style={{marginLeft: 20, marginRight: 20}}>
                    <br/>
                    <Row justify='center'><Col><Title style={{justifyContent : 'center'}}>{offDcdNm()}</Title></Col></Row>
                    <br/>
                    <Row justify='end'>
                        <Space size='middle'>
                            {approvalList && approvalList.length > 0 ?
                            <Col>
                                <Descriptions bordered size="small" layout="vertical" column={5}>
                                    <Descriptions.Item label="신청자" key={offReqInfo.empNo}><Row style={{display: "flex", justifyContent: "center", alignItems: "center"}}>{common.isEmptyString(offReqInfo.apprNo) ? common.getUserNm() : offReqInfo.userNm}</Row><Row style={{display: "flex", justifyContent: "center", alignItems: "center"}}>{offReqInfo.apprNo ? offReqInfo.mainApprStCd === '08' ? '(취소)' : '(상신)' : '(대기)'}</Row></Descriptions.Item>
                                        {approvalList.map(e => {
                                            return <Descriptions.Item label="승인자" key={e.apprEmpNo}><Row style={{display: "flex", justifyContent: "center", alignItems: "center"}}>{e.apprUserNm}</Row><Row style={{display: "flex", justifyContent: "center", alignItems: "center"}}>({e.apprStNm})</Row></Descriptions.Item>
                                        })}
                                        {!approvalList || approvalList.length < 1 ? <Descriptions.Item label="승인자"> </Descriptions.Item> : null}
                                </Descriptions>
                            </Col> : null}
                            {refList && refList.length > 0 ?
                            <Col>
                                <Descriptions bordered size="small" layout="vertical" column={5}>
                                    {refList.map(e => {
                                        return <Descriptions.Item label="참조자" key={e.refEmpNo}><Row style={{display: "flex", justifyContent: "center", alignItems: "center"}}>{e.refUserNm}</Row><Row style={{display: "flex", justifyContent: "center", alignItems: "center"}}>({e.chkYn ? e.chkYn === "Y" ? "읽음" : "안읽음" : "대기"})</Row></Descriptions.Item>
                                    })}
                                </Descriptions>
                            </Col> : null}
                        </Space>
                    </Row>
                    <Row>
                        <Col style={{width:'100%', marginTop: 10}}>
                            <Descriptions bordered column={1} size='small'>
                                <Descriptions.Item label='신청인'>{common.isEmptyString(offReqInfo.apprNo) ? common.getUserNm() : offReqInfo.userNm}</Descriptions.Item>
                                <Descriptions.Item label='휴가구분'> {
                                    common.isEmptyString(offReqInfo.apprNo) ? 
                                        <Select
                                            style={{
                                                width: 200,
                                            }}
                                            optionFilterProp='label'
                                            options={offDcdList}
                                            defaultValue={fn_CurrOffDcd().value}
                                            onChange={val => {
                                                dispatch(Usr0102mAction.setOffReqInfo({...offReqInfo, offDcd: val}))
                                            }}
                                            value={offReqInfo.offDcd}
                                        />
                                        : 
                                        offDcdNm()
                                }</Descriptions.Item>
                                <Descriptions.Item label='기간'>
                                    <Space direction='vertical'>
                                        {fn_CurrOffDcd().attr2 !== '1' ?
                                        <>
                                        {common.isEmptyString(offReqInfo.apprNo) ?
                                        <ConfigProvider locale={locale}>
                                            <DatePicker inputReadOnly disabled={!common.isEmptyString(offReqInfo.apprNo)} allowClear={false} value={dayjs(offReqInfo.offStDt)} onChange={calendar_onChange}/> 
                                        </ConfigProvider>
                                        : commonDate.maskDate(offReqInfo.offStDt, 'YYYY-MM-DD')}
                                        </>
                                        :
                                        <>
                                        {common.isEmptyString(offReqInfo.apprNo) ?
                                        <ConfigProvider locale={locale}>
                                            <RangePicker
                                                style={{ width: 305 }}
                                                id='tx_calendar'
                                                allowClear={false}
                                                onChange={calendar_onChange}
                                                picker='date'
                                                value={[dayjs(offReqInfo.offStDt), dayjs(offReqInfo.offEdDt)]}
                                            />
                                        </ConfigProvider>
                                         : commonDate.maskDate(offReqInfo.offStDt, 'YYYY-MM-DD') + ' ~ ' + commonDate.maskDate(offReqInfo.offEdDt, 'YYYY-MM-DD')}</>
                                        }
                                        <Space>
                                            현재잔여연차 {offReqInfo.remainOffDays} 일 /
                                            총 {fn_GetUseDays()} 일 (휴일은 신청 일수에 포함되지 않습니다.)
                                        </Space>
                                    </Space>
                                </Descriptions.Item>
                                <Descriptions.Item label='사유'>
                                    {common.isEmptyString(offReqInfo.apprNo) ? <TextArea id='inputOffRsn' value={offReqInfo.offRsn} autoSize={{ minRows: 3 }} style={{ width: '100%'}} onChange={e => { dispatch(Usr0102mAction.setOffReqInfo({ ...offReqInfo, offRsn : e.currentTarget.value })) }}/> : <div id='inputOffRsn' style={{whiteSpace: 'pre', minHeight: 60}}>{offReqInfo.offRsn}</div>}
                                </Descriptions.Item>
                                <Descriptions.Item label='행선지'>
                                    {common.isEmptyString(offReqInfo.apprNo) ? <Input id='inputOffDest' autoComplete='off' value={offReqInfo.dest} style={{ width: '100%'}} onChange={e => { dispatch(Usr0102mAction.setOffReqInfo({ ...offReqInfo, dest : e.currentTarget.value })) }}/> : offReqInfo.dest}
                                </Descriptions.Item>
                                <Descriptions.Item label='연락처'>
                                    {common.isEmptyString(offReqInfo.apprNo) ? <Input id='inputContact' autoComplete='off' value={offReqInfo.contact} style={{ width: '100%'}} onChange={e => { dispatch(Usr0102mAction.setOffReqInfo({ ...offReqInfo, contact : e.currentTarget.value })) }}/> : offReqInfo.contact}
                                </Descriptions.Item>
                                <Descriptions.Item label='대체근무자'>
                                    {common.isEmptyString(offReqInfo.apprNo) ? <Input id='inputBackupUser' autoComplete='off' value={offReqInfo.backupUser} style={{ width: '100%'}} onChange={e => { dispatch(Usr0102mAction.setOffReqInfo({ ...offReqInfo, backupUser : e.currentTarget.value })) }}/> : offReqInfo.backupUser}
                                </Descriptions.Item>
                            </Descriptions>
                        </Col>
                    </Row>
                    <br/>
                    <br/>
                    <Row justify='center'><Col>상기와 같은 사유로 인하여 {offDcdNm()}계를 제출하오니 재가바랍니다.</Col></Row>
                </div>
            </Content>
        </Layout>
    );
}

export default Usr0102m;
